import React, { useCallback, useRef, useState } from "react";
import { FileUp, FileImage, FileText, File as FileIcon } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector } from "../../../redux/hooks.ts";
import { validToUpload } from "../fixtures/validToUpload.ts";
import { cn } from "../../../lib/utils";
import { formatUploadLimit } from "../../../utils/vault/verificationUploadLimits";
import {
  MAX_IMAGE_BYTES,
  UPLOAD_POLICIES,
} from "../../../constants/uploadPolicies";

interface Props {
  name: string;
  label: string;
  type: "image" | "pdf" | "both";
  maxFileBytes?: number;
  maxImageBytes?: number;
  onFileSelect: (file: File) => void;
}

const acceptMap: Record<string, { allowed: string; txt: string }> = {
  image: {
    allowed: "image/jpeg,image/jpg,image/png,image/webp",
    txt: "una imagen",
  },
  pdf: { allowed: "application/pdf,.pdf", txt: "un PDF" },
  both: {
    allowed: "image/jpeg,image/jpg,image/png,image/webp,application/pdf,.pdf",
    txt: "un documento",
  },
};

const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const iconMap: Record<string, React.ReactNode> = {
  image: <FileImage className="h-8 w-8 text-gray-400" />,
  pdf: <FileText className="h-8 w-8 text-gray-400" />,
  both: <FileIcon className="h-8 w-8 text-gray-400" />,
};

const isAcceptedFile = (
  file: File,
  type: "image" | "pdf" | "both",
): boolean => {
  const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
  const isPdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

  if (type === "image") return isImage;
  if (type === "pdf") return isPdf;
  return isImage || isPdf;
};

export const FileDrop: React.FC<Props> = ({
  name: _name,
  label,
  type,
  maxFileBytes = UPLOAD_POLICIES.simple.max_file_bytes,
  maxImageBytes = MAX_IMAGE_BYTES,
  onFileSelect,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const { activeRequirement } = useAppSelector((state) => state.vault);
  const enabled = validToUpload.includes(activeRequirement?.status);

  const { allowed, txt } = acceptMap[type];
  const icon = iconMap[type];
  const limitLabel =
    type === "image"
      ? formatUploadLimit(maxImageBytes)
      : type === "pdf"
        ? formatUploadLimit(maxFileBytes)
        : maxImageBytes === maxFileBytes
          ? formatUploadLimit(maxFileBytes)
          : `Imágenes: ${formatUploadLimit(maxImageBytes)} · PDF: ${formatUploadLimit(maxFileBytes)}`;

  const validateAndSelect = useCallback(
    (file: File) => {
      if (!isAcceptedFile(file, type)) {
        toast.error(`Formato no válido. Solo se permite ${txt}`);
        return;
      }
      const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
      const applicableLimit = isImage ? maxImageBytes : maxFileBytes;
      if (file.size > applicableLimit) {
        toast.error(
          `Archivo muy grande. Máximo: ${formatUploadLimit(applicableLimit)}`,
        );
        return;
      }
      onFileSelect(file);
    },
    [type, txt, maxFileBytes, maxImageBytes, onFileSelect],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) validateAndSelect(file);
      e.target.value = "";
    },
    [validateAndSelect],
  );

  const handleClick = useCallback(() => {
    if (enabled) fileInputRef.current?.click();
  }, [enabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (!enabled) return;
      const file = e.dataTransfer.files?.[0];
      if (file) validateAndSelect(file);
    },
    [enabled, validateAndSelect],
  );

  return (
    <div className="w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowed}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Dropzone */}
      <div
        role="button"
        tabIndex={enabled ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "relative flex flex-col items-center justify-center gap-3",
          "rounded-lg border-2 border-dashed",
          "min-h-[180px] p-6",
          "transition-all duration-150",
          "focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2",
          isDragActive
            ? "border-gray-400 bg-gray-100"
            : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100",
          !enabled && "cursor-not-allowed opacity-50",
          enabled && "cursor-pointer",
        )}
      >
        <div
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-lg",
            "bg-white border border-gray-200",
          )}
        >
          {isDragActive ? <FileUp className="h-5 w-5 text-gray-500" /> : icon}
        </div>

        <div className="text-center">
          <p className="text-sm font-medium text-gray-600">
            {isDragActive ? "Suelta aquí" : label}
          </p>
          {!isDragActive && (
            <>
              <p className="text-xs text-gray-400 mt-1">
                Selecciona o arrastra {txt}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Límite por archivo: {limitLabel}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
